import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "recording-management" }
const _hoisted_2 = { class: "search-section" }
const _hoisted_3 = { class: "search-form" }
const _hoisted_4 = { class: "button-group" }
const _hoisted_5 = { class: "recording-list" }
const _hoisted_6 = { class: "card-header" }
const _hoisted_7 = { class: "header-actions" }
const _hoisted_8 = { class: "action-buttons" }
const _hoisted_9 = { class: "table-footer" }
const _hoisted_10 = { class: "audio-player" }
const _hoisted_11 = ["src"]
const _hoisted_12 = {
  key: 0,
  class: "recording-info"
}

import { ref, reactive, onMounted, nextTick } from "vue";
import {
  Search,
  Refresh,
  Delete,
  Download,
  VideoPlay,
} from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { useStore } from "vuex";
import { formatDateTime } from "@/utils/dateTimeUtils";
import { DataService } from "@/services/dataService";
import config from "@/config";

// 中文注释：引入全局store
type TagType = "primary" | "success" | "warning" | "info" | "danger";

export default /*@__PURE__*/_defineComponent({
  __name: 'RecordingManagement',
  setup(__props) {

const store = useStore();

// 权限检查函数
const hasPermission = (perm: string) => store.getters.hasPermission(perm);

// 响应式数据
const loading = ref(false);
const playDialogVisible = ref(false);
const currentRecording = ref<any | null>(null);
const selectedRecordings = ref<any[]>([]);
const audioPlayer = ref<HTMLAudioElement | null>(null);

// 搜索表单
const searchForm = reactive({
  device_id: "",
  operator_name: "",
  dateRange: [] as string[],
});

// 分页数据
const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0,
});

// 录音列表数据
const recordings = ref<any[]>([]);

// 获取录音列表（真实接口）
const fetchRecordings = async () => {
  loading.value = true;
  try {
    const params: any = {
      page: pagination.page,
      size: pagination.pageSize,
      device_id: searchForm.device_id || undefined,
      operator_name: searchForm.operator_name || undefined,
      start_date: searchForm.dateRange?.[0] || undefined,
      end_date: searchForm.dateRange?.[1] || undefined,
    };
    // 直接调用后端REST：/api/v1/recordings
    const url = config.apiUrls
      ? (config.apiUrls as any)["RECORDINGS"]
      : undefined;
    const requestUrl = url || `${config.apiBaseUrl}/recordings`;
    const data = await (
      await import("@/services/apiClient")
    ).default.get(requestUrl, { params });
    // 期望返回 DataResponse<{ total, recordings }>
    const payload = data?.data?.data || data?.data || {};
    const list = Array.isArray(payload.recordings) ? payload.recordings : [];
    // 中文注释：前端隐藏已删除的录音项，不改动后端
    recordings.value = list.filter((r: any) => r?.status !== "deleted");
    pagination.total = payload.total || 0;
  } catch (error) {
    console.error("获取录音列表失败:", error);
    ElMessage.error("Failed to load recordings");
  } finally {
    loading.value = false;
  }
};

// 搜索
const handleSearch = () => {
  pagination.page = 1;
  fetchRecordings();
};

// 重置搜索
const resetSearch = () => {
  searchForm.device_id = "";
  searchForm.operator_name = "";
  searchForm.dateRange = [] as any;
  handleSearch();
};

// 刷新录音列表
const refreshRecordings = () => {
  fetchRecordings();
};

// 播放录音：获取直链并在对话框中播放
const playRecording = async (recording: any) => {
  try {
    if (!recording?.recording_id && !recording?.id) {
      ElMessage.error("Invalid recording id");
      return;
    }
    const rid = recording.recording_id || recording.id;
    const baseUrl = `${config.apiBaseUrl}/recordings`;
    const client = (await import("@/services/apiClient")).default;
    const resp = await client.get(`${baseUrl}/${rid}/download-url`);
    const url = resp?.data?.data?.url || resp?.data?.url;
    if (!url) {
      ElMessage.error("Failed to get playback url");
      return;
    }
    currentRecording.value = { ...(recording || {}), audio_url: url };
    playDialogVisible.value = true;
    await nextTick();
    try {
      const audio = audioPlayer.value;
      if (audio) {
        audio.src = url;
        await audio.play();
      }
    } catch (e) {
      console.warn("Auto play failed; user interaction may be required.", e);
    }
  } catch (error) {
    console.error("播放失败:", error);
    ElMessage.error("Play failed");
  }
};

// 关闭播放对话框
const closePlayDialog = () => {
  playDialogVisible.value = false;
  currentRecording.value = null;
};

// 下载录音（先请求鉴权接口获取直链，优先弹出保存对话框）
const downloadRecording = async (recording: any) => {
  try {
    if (!recording?.recording_id && !recording?.id) {
      ElMessage.error("Invalid recording id");
      return;
    }
    const rid = recording.recording_id || recording.id;
    const baseUrl = `${config.apiBaseUrl}/recordings`;
    const client = (await import("@/services/apiClient")).default;
    const resp = await client.get(`${baseUrl}/${rid}/download-url`);
    const url = resp?.data?.data?.url || resp?.data?.url;
    if (!url) {
      ElMessage.error("Failed to get download url");
      return;
    }

    const suggestedName = `${recording?.recording_id || rid}.mp3`;

    // 优先使用 File System Access API
    // 兼容性回退：使用 <a download> 触发浏览器保存
    const anyWindow: any = window as any;
    if (anyWindow && typeof anyWindow.showSaveFilePicker === "function") {
      try {
        const handle = await anyWindow.showSaveFilePicker({
          suggestedName,
          types: [
            {
              description: "Audio MP3",
              accept: { "audio/mpeg": [".mp3"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        const respBin = await fetch(url, { method: "GET" });
        const blob = await respBin.blob();
        await writable.write(blob);
        await writable.close();
        ElMessage.success("Saved successfully");
        return;
      } catch (e) {
        // 若用户取消或写入失败，回退为普通下载
      }
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    ElMessage.success("Starting download...");
  } catch (error) {
    console.error("下载失败:", error);
    ElMessage.error("Download failed");
  }
};

// 删除录音
const deleteRecording = async (recording: any) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除录音 ${recording.recording_id || recording.id} 吗？`,
      "确认删除",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    const rid = recording.recording_id || recording.id;
    const baseDeleteUrl =
      (config.apiUrls && (config.apiUrls as any)["RECORDINGS"]) ||
      `${config.apiBaseUrl}/recordings`;
    await (
      await import("@/services/apiClient")
    ).default.delete(`${baseDeleteUrl}/${rid}`);
    ElMessage.success("Deleted successfully");
    fetchRecordings();
  } catch (error: any) {
    if (error !== "cancel") {
      console.error("删除失败:", error);
      ElMessage.error("Delete failed");
    }
  }
};

// 批量删除
const batchDelete = async () => {
  if (selectedRecordings.value.length === 0) {
    ElMessage.warning("请选择要删除的录音");
    return;
  }

  try {
    await ElMessageBox.confirm(
      `确定要删除选中的 ${selectedRecordings.value.length} 个录音吗？`,
      "确认批量删除",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    const ids = selectedRecordings.value.map((r) => r.recording_id || r.id);
    // 后端如需批量删除API可扩展；当前逐个删除
    const client = (await import("@/services/apiClient")).default;
    const baseDeleteUrl =
      (config.apiUrls && (config.apiUrls as any)["RECORDINGS"]) ||
      `${config.apiBaseUrl}/recordings`;
    await Promise.all(
      ids.map((rid: string) => client.delete(`${baseDeleteUrl}/${rid}`))
    );
    ElMessage.success("Batch deleted successfully");
    fetchRecordings();
  } catch (error: any) {
    if (error !== "cancel") {
      console.error("批量删除失败:", error);
      ElMessage.error("Batch delete failed");
    }
  }
};

// 选择变化
const handleSelectionChange = (selection: any[]) => {
  selectedRecordings.value = selection;
};

// 分页处理
const handleSizeChange = (size: number) => {
  pagination.pageSize = size;
  pagination.page = 1;
  fetchRecordings();
};

const handleCurrentChange = (page: number) => {
  pagination.page = page;
  fetchRecordings();
};

// 工具函数
const formatDuration = (seconds?: number) => {
  const total = Number(seconds || 0);
  const minutes = Math.floor(total / 60);
  const remainingSeconds = total % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const formatFileSize = (bytes?: number) => {
  const b = Number(bytes || 0);
  if (b === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"] as const;
  const i = Math.floor(Math.log(b) / Math.log(k));
  return parseFloat((b / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const getStatusType = (status: string): TagType => {
  const statusMap: Record<string, TagType> = {
    completed: "success",
    processing: "warning",
    failed: "danger",
    recording: "info",
  };
  return statusMap[status] || "info";
};

const getStatusText = (status: string) => {
  const statusMap: Record<string, string> = {
    completed: "已完成",
    processing: "处理中",
    failed: "失败",
    recording: "录制中",
  };
  return statusMap[status] || status;
};

// 页面加载时获取数据
onMounted(() => {
  fetchRecordings();
});

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_el_card, { class: "search-card" }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_el_row, { gutter: 12 }, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 5 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_input, {
                      modelValue: searchForm.device_id,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchForm.device_id) = $event)),
                      placeholder: "设备编号",
                      clearable: ""
                    }, {
                      prefix: _withCtx(() => [
                        _createVNode(_component_el_icon, null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Search))
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 5 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_input, {
                      modelValue: searchForm.operator_name,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchForm.operator_name) = $event)),
                      placeholder: "联络员",
                      clearable: ""
                    }, {
                      prefix: _withCtx(() => [
                        _createVNode(_component_el_icon, null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Search))
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 6 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_date_picker, {
                      modelValue: searchForm.dateRange,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchForm.dateRange) = $event)),
                      type: "daterange",
                      "range-separator": "至",
                      "start-placeholder": "开始日期",
                      "end-placeholder": "结束日期",
                      format: "YYYY-MM-DD",
                      "value-format": "YYYY-MM-DD",
                      style: {"width":"100%"}
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 6 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_4, [
                      (hasPermission('recording.view'))
                        ? (_openBlock(), _createBlock(_component_el_button, {
                            key: 0,
                            type: "primary",
                            onClick: handleSearch
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_el_icon, null, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(Search))
                                ]),
                                _: 1
                              }),
                              _cache[6] || (_cache[6] = _createTextVNode(" 搜索 ", -1))
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      (hasPermission('recording.view'))
                        ? (_openBlock(), _createBlock(_component_el_button, {
                            key: 1,
                            onClick: resetSearch
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_el_icon, null, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(Refresh))
                                ]),
                                _: 1
                              }),
                              _cache[7] || (_cache[7] = _createTextVNode(" 重置 ", -1))
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(_component_el_card, { class: "list-card" }, {
        header: _withCtx(() => [
          _createElementVNode("div", _hoisted_6, [
            _cache[9] || (_cache[9] = _createElementVNode("span", null, "录音列表", -1)),
            _createElementVNode("div", _hoisted_7, [
              (hasPermission('recording.delete'))
                ? (_openBlock(), _createBlock(_component_el_button, {
                    key: 0,
                    type: "danger",
                    onClick: batchDelete,
                    disabled: selectedRecordings.value.length === 0
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Delete))
                        ]),
                        _: 1
                      }),
                      _cache[8] || (_cache[8] = _createTextVNode(" 批量删除 ", -1))
                    ]),
                    _: 1
                  }, 8, ["disabled"]))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        default: _withCtx(() => [
          _withDirectives((_openBlock(), _createBlock(_component_el_table, {
            data: recordings.value,
            style: {"width":"100%"},
            "table-layout": "fixed",
            onSelectionChange: handleSelectionChange
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_table_column, {
                type: "selection",
                width: "55"
              }),
              _createVNode(_component_el_table_column, {
                prop: "device_id",
                label: "设备编号",
                width: "160"
              }),
              _createVNode(_component_el_table_column, {
                prop: "recording_id",
                label: "录音ID",
                width: "180",
                "show-overflow-tooltip": ""
              }),
              _createVNode(_component_el_table_column, {
                prop: "start_time",
                label: "开始时间",
                width: "180"
              }, {
                default: _withCtx((scope) => [
                  _createTextVNode(_toDisplayString(_unref(formatDateTime)(scope.row.start_time)), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                prop: "duration_seconds",
                label: "时长",
                width: "80"
              }, {
                default: _withCtx((scope) => [
                  _createTextVNode(_toDisplayString(formatDuration(scope.row.duration_seconds)), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                prop: "status",
                label: "状态",
                width: "90"
              }, {
                default: _withCtx((scope) => [
                  _createVNode(_component_el_tag, {
                    type: getStatusType(scope.row.status)
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(getStatusText(scope.row.status)), 1)
                    ]),
                    _: 2
                  }, 1032, ["type"])
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                label: "操作",
                width: "200",
                fixed: "right"
              }, {
                default: _withCtx((scope) => [
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_component_el_button, {
                      type: "primary",
                      size: "small",
                      onClick: ($event: any) => (playRecording(scope.row))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_icon, null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(VideoPlay))
                          ]),
                          _: 1
                        }),
                        _cache[10] || (_cache[10] = _createTextVNode(" 播放 ", -1))
                      ]),
                      _: 1
                    }, 8, ["onClick"]),
                    _createVNode(_component_el_button, {
                      type: "success",
                      size: "small",
                      onClick: ($event: any) => (downloadRecording(scope.row))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_icon, null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Download))
                          ]),
                          _: 1
                        }),
                        _cache[11] || (_cache[11] = _createTextVNode(" 下载 ", -1))
                      ]),
                      _: 1
                    }, 8, ["onClick"]),
                    (hasPermission('recording.delete'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 0,
                          type: "danger",
                          size: "small",
                          onClick: ($event: any) => (deleteRecording(scope.row))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_icon, null, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Delete))
                              ]),
                              _: 1
                            }),
                            _cache[12] || (_cache[12] = _createTextVNode(" 删除 ", -1))
                          ]),
                          _: 1
                        }, 8, ["onClick"]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["data"])), [
            [_directive_loading, loading.value]
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createVNode(_component_el_pagination, {
              "current-page": pagination.page,
              "onUpdate:currentPage": _cache[3] || (_cache[3] = ($event: any) => ((pagination.page) = $event)),
              "page-size": pagination.pageSize,
              "onUpdate:pageSize": _cache[4] || (_cache[4] = ($event: any) => ((pagination.pageSize) = $event)),
              "page-sizes": [10, 20, 50, 100],
              total: pagination.total,
              layout: "total, sizes, prev, pager, next, jumper",
              onSizeChange: handleSizeChange,
              onCurrentChange: handleCurrentChange
            }, null, 8, ["current-page", "page-size", "total"])
          ])
        ]),
        _: 1
      })
    ]),
    _createVNode(_component_el_dialog, {
      modelValue: playDialogVisible.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((playDialogVisible).value = $event)),
      title: "播放录音",
      width: "600px",
      onClose: closePlayDialog
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("audio", {
            ref_key: "audioPlayer",
            ref: audioPlayer,
            controls: "",
            style: {"width":"100%"},
            src: currentRecording.value?.audio_url
          }, " 您的浏览器不支持音频播放 ", 8, _hoisted_11)
        ]),
        (currentRecording.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _createElementVNode("p", null, [
                _cache[13] || (_cache[13] = _createElementVNode("strong", null, "设备编号:", -1)),
                _createTextVNode(" " + _toDisplayString(currentRecording.value.device_id), 1)
              ]),
              _createElementVNode("p", null, [
                _cache[14] || (_cache[14] = _createElementVNode("strong", null, "录音ID:", -1)),
                _createTextVNode(" " + _toDisplayString(currentRecording.value.recording_id), 1)
              ]),
              _createElementVNode("p", null, [
                _cache[15] || (_cache[15] = _createElementVNode("strong", null, "开始时间:", -1)),
                _createTextVNode(" " + _toDisplayString(_unref(formatDateTime)(currentRecording.value.start_time)), 1)
              ]),
              _createElementVNode("p", null, [
                _cache[16] || (_cache[16] = _createElementVNode("strong", null, "时长:", -1)),
                _createTextVNode(" " + _toDisplayString(formatDuration(currentRecording.value.duration_seconds)), 1)
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})